import { FormControlInputVariant } from 'omakase/FormControls/FormControlInputCore';
import { Theme, css } from '@emotion/react';

export const formControl = css`
  display: flex;
  flex-direction: column-reverse; // For tab order purposes - the input will be focused before the label adornment (if exists)
`;

export const labelContainer = ({ spacing }: Theme) => css`
  align-items: center;
  display: flex;
  margin-bottom: ${spacing(1)}px;
  justify-content: space-between;
`;

export const labelStyles = ({ colors, typography }: Theme) => css`
  color: ${colors.fgDefault};
  position: static;
  transform: none;
  ${typography.variant('captionStrong')};
  &.Mui-focused {
    color: ${colors.fgDefault};
  }
  &.Mui-error {
    color: ${colors.fgDefault};
  }
  &.Mui-disabled {
    color: ${colors.fgSubtle};
  }
  &.MuiInputLabel-animated {
    transition: none;
  }
  & + .MuiInputBase-root {
    margin-top: 0;
  }
`;

const getSharedInputBaseStyles = (
  { colors, typography }: Theme,
  variant: FormControlInputVariant
) => css`
  background: ${variant === 'basic' ? colors.bgLighter : colors.bgDefault};
  box-shadow: inset 0 0 0 1px ${colors.borderDefault};

  &.Mui-disabled {
    background: ${colors.bgLighter};
    box-shadow: none;
    color: ${colors.fgSubtle};
  }
  &.Mui-error {
    box-shadow: 0 0 0 2px ${colors.borderDangerBase};
  }

  & .MuiInputBase-input {
    caret-color: ${colors.bgPrimaryBase};
    height: auto;
    padding: 14px 48px 14px 12px;
    text-overflow: ellipsis;
    ${typography.variant('body02')};
  }
`;

// For TextInput
export const inputBase = (theme: Theme, variant: FormControlInputVariant) => [
  getSharedInputBaseStyles(theme, variant),
  css`
    &.Mui-focused {
      box-shadow: 0 0 0 2px ${theme.colors.borderPrimaryBase};
    }
  `,
];

// For Select
export const inputBaseSelect = (
  theme: Theme,
  variant: FormControlInputVariant,
  { placeholderActive }: { placeholderActive: boolean }
) => [
  getSharedInputBaseStyles(theme, variant),
  css`
    color: ${theme.colors.fgDefault};

    // Override MUI styles for Select component (specifically, a thick underline)
    &::before {
      content: none;
    }
    &::after {
      content: none;
    }

    &.Mui-focused > .MuiSelect-select {
      background: ${variant === 'basic' ? theme.colors.bgLighter : theme.colors.bgDefault};
      box-shadow: 0 0 0 2px ${theme.colors.borderPrimaryBase};
    }

    & .MuiSelect-root {
      background: none;
    }

    & .MuiSelect-icon {
      margin-right: ${theme.spacing(2)}px;
    }
  `,
];

export const startAdornmentStyles = ({ spacing }: Theme) => css`
  margin-left: ${spacing(2)}px;
  margin-right: 0;
`;

export const endAdornmentStyles = ({ spacing }: Theme) => css`
  margin-right: ${spacing(2)}px;
  margin-left: 0;
`;

export const helperTextStyles = (
  { colors, typography }: Theme,
  { warn, valid }: { warn: boolean; valid: boolean }
) => css`
  color: ${valid ? colors.fgSuccessBase : warn ? colors.fgWarningBase : colors.fgSubtle};
  margin-top: 8px;
  ${typography.variant('caption')};
  &.Mui-error {
    color: ${colors.fgDangerBase};
  }
  &.Mui-disabled {
    color: ${colors.fgSubtle};
  }
`;
